import { useState, useMemo, useCallback, useEffect, Suspense } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useAuth } from '@/contexts/AuthContext';
import { useSpaceData } from '@/hooks/useSpaceData';
import { ComplianceExplorer, ComplianceFilterContent } from './components';

// Lazy load PoliciesFolder for better performance
const PoliciesFolder = lazyWithRetry(() => import('./components/PoliciesFolder'));

// Folder slug to internal name mapping
const FOLDER_SLUGS = {
  'certificates': 'personnel',
  'site-documents': 'site',
  'policies': 'policies'
};

// Internal name to folder slug mapping
const FOLDER_TO_SLUG = {
  'personnel': 'certificates',
  'site': 'site-documents',
  'policies': 'policies'
};

// Folder display names
const FOLDER_NAMES = {
  'personnel': 'User Certificates',
  'site': 'Site Documents',
  'policies': 'Policies'
};

export default function DocsAndFilesPage({ stackId, stackName, stackProjects, isStackView = false, isGlobalView = false }) {
  const { projectId, folder: folderSlug } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');

  // Use the space data hook to get all tools for the space (skip for global view)
  const { space, spaceTools } = useSpaceData(
    isGlobalView ? null : projectId,
    userProfile?.id,
    company?.id
  );

  // Set page title
  usePageTitle(
    isGlobalView ? 'Documents' :
    isStackView ? `${stackName} Documents` :
    space?.name ? `${space.name} Documents` : 'Documents'
  );

  // Convert URL slug to internal folder name
  const currentFolder = folderSlug ? FOLDER_SLUGS[folderSlug] || null : null;

  // State for search, filters, and page size
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize(25));
  const [stats, setStats] = useState(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInputValue]);

  // Reset filters when folder changes
  useEffect(() => {
    setSearchInputValue('');
    setSearchQuery('');
    setStatusFilter(null);
  }, [currentFolder]);

  // Handle folder navigation
  const handleFolderChange = useCallback((folder) => {
    if (folder) {
      const slug = FOLDER_TO_SLUG[folder];
      if (isGlobalView) {
        navigate(`/docs/${slug}`);
      } else if (isStackView) {
        navigate(`/stack/${stackId}/docs/${slug}`);
      } else {
        navigate(`/space/${projectId}/docs/${slug}`);
      }
    } else {
      // Go back to root
      if (isGlobalView) {
        navigate(`/docs`);
      } else if (isStackView) {
        navigate(`/stack/${stackId}/docs`);
      } else {
        navigate(`/space/${projectId}/docs`);
      }
    }
  }, [navigate, projectId, stackId, isStackView, isGlobalView]);

  // Build breadcrumbs for ToolHeader
  const breadcrumbs = useMemo(() => {
    if (!currentFolder) return null;

    return [{
      name: FOLDER_NAMES[currentFolder],
      onToolClick: () => handleFolderChange(null) // Click on "Docs" goes back to root
    }];
  }, [currentFolder, handleFolderChange]);

  // Build filter content for popover
  const filterContent = useMemo(() => {
    if (!currentFolder) return null;

    return (
      <ComplianceFilterContent
        currentFolder={currentFolder}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        pageSize={pageSize}
        onPageSizeChange={(val) => { setPageSize(val); setStoredPageSize(val); }}
        stats={stats}
      />
    );
  }, [currentFolder, statusFilter, pageSize, stats]);

  // Build status info (count badges) for ToolHeader
  const statusInfo = useMemo(() => {
    if (!currentFolder || !stats) return null;

    // Handle policies folder separately
    if (currentFolder === 'policies') {
      const policyStats = stats.policies || {};
      return (
        <div className="flex items-center gap-2 text-xs mr-2">
          <span className="text-gray-500">{policyStats.total || 0} policies</span>
          {policyStats.overdue > 0 && (
            <span className="text-red-600 font-medium">{policyStats.overdue} overdue</span>
          )}
          {policyStats.pending > 0 && (
            <span className="text-amber-600 font-medium">{policyStats.pending} pending</span>
          )}
        </div>
      );
    }

    const filteredTotal = currentFolder === 'personnel'
      ? (stats.personnel?.filteredTotal || stats.personnel?.total || 0)
      : (stats.site?.filteredTotal || stats.site?.total || 0);

    return (
      <div className="flex items-center gap-2 text-xs mr-2">
        <span className="text-gray-500">{filteredTotal} items</span>
        {currentFolder === 'personnel' && (
          <>
            {stats.personnel?.expired > 0 && (
              <span className="text-red-600 font-medium">{stats.personnel.expired} expired</span>
            )}
            {stats.personnel?.expiring > 0 && (
              <span className="text-amber-600 font-medium">{stats.personnel.expiring} expiring</span>
            )}
            {stats.personnel?.missing > 0 && (
              <span className="text-gray-400">{stats.personnel.missing} outstanding</span>
            )}
          </>
        )}
        {currentFolder === 'site' && (
          <>
            {stats.site?.overdue > 0 && (
              <span className="text-red-600 font-medium">{stats.site.overdue} overdue</span>
            )}
            {stats.site?.dueSoon > 0 && (
              <span className="text-amber-600 font-medium">{stats.site.dueSoon} due soon</span>
            )}
            {stats.site?.notStarted > 0 && (
              <span className="text-gray-400">{stats.site.notStarted} outstanding</span>
            )}
          </>
        )}
      </div>
    );
  }, [currentFolder, stats]);

  return (
    <AppShell3>
      <PageTransition>
        {/* ToolHeader */}
        <ToolHeader
          spaceName={isGlobalView ? 'All Documents' : isStackView ? stackName : (space?.name || 'Space')}
          spaceImage={isGlobalView ? null : space?.avatar}
          toolName="Docs"
          currentTool="docs"
          spaceTools={isGlobalView ? [] : (spaceTools || [])}
          stackId={isStackView ? stackId : null}
          stackProjects={isStackView ? stackProjects : null}
          isStackView={isStackView}
          isGlobalView={isGlobalView}
          selectedSpaceFilter={selectedSpaceFilter}
          onSpaceFilterChange={setSelectedSpaceFilter}
          // New props for extended functionality
          breadcrumbs={breadcrumbs}
          searchValue={searchInputValue}
          onSearchChange={currentFolder ? setSearchInputValue : null}
          searchPlaceholder={
            currentFolder === 'personnel' ? 'Search certificates...' :
            currentFolder === 'policies' ? 'Search policies...' :
            'Search documents...'
          }
          filterContent={currentFolder !== 'policies' ? filterContent : null}
          showFiltersButton={!!currentFolder && currentFolder !== 'policies'}
          statusInfo={statusInfo}
        />

        {/* File Explorer View */}
        <div className="flex-1 overflow-auto p-4">
          <div className="max-w-7xl mx-auto">
            {currentFolder === 'policies' ? (
              <Suspense fallback={
                <div className="h-full flex flex-col bg-white rounded-lg border">
                  <div className="flex-1 p-4 space-y-2">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
                    ))}
                  </div>
                </div>
              }>
                <PoliciesFolder
                  projectId={projectId}
                  isStackView={isStackView}
                  isGlobalView={isGlobalView}
                  stackId={stackId}
                  onFolderChange={handleFolderChange}
                  searchQuery={searchQuery}
                  onStatsChange={setStats}
                />
              </Suspense>
            ) : (
              <ComplianceExplorer
                projectId={projectId}
                projectIds={stackProjects?.map(p => p.id)}
                isStackView={isStackView}
                isGlobalView={isGlobalView}
                selectedSpaceFilter={selectedSpaceFilter}
                // Controlled state
                currentFolder={currentFolder}
                onFolderChange={handleFolderChange}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                pageSize={pageSize}
                onStatsChange={setStats}
              />
            )}
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
